import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "@/utils/toast.utils";
import { captureException, sendCountMetric, SentryMetric } from "@/utils/sentry";

export const useLoginForm = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { signIn } = useAuth();

  // Define login form schema
  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t("errors.required"))
      .email({ message: t("errors.invalidEmail") }),
    password: z.string().min(1, t("errors.required")),
  });

  type LoginFormValues = z.infer<typeof loginSchema>;

  // Initialize the form with zod resolver
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onBlur",
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);

    try {
      await signIn(values.email, values.password);
      sendCountMetric(SentryMetric.LOGIN_EMAIL_AND_PASSWORD_SUCCESS);
    } catch (error) {
      sendCountMetric(SentryMetric.LOGIN_EMAIL_AND_PASSWORD_FAILURE);
      console.error("Login error:", error);
      captureException(error as Error, {
        extra: {
          component: 'useLoginForm',
          action: 'handleSubmit',
        },
      });
      toast.error(t("auth.signInError"));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    handleSubmit,
    isLoading,
  };
}; 